"use client";

/**
 * LiquidLogoForm
 * Animasi "cairan berubah jadi logo" untuk Next.js (App Router / Pages Router).
 *
 * Alur animasi (default 3,2 detik):
 *   1. Drops     tetesan oranye masuk dari kiri dan bergabung jadi satu gumpalan
 *   2. Flow      gumpalan memanjang jadi aliran yang mengitari jalur orbit logo
 *   3. Fill      cairan dituang mengisi huruf AiBAS dari kiri ke kanan
 *   4. Solidify  riak mereda, cairan "mendingin" jadi warna asli logo, halo gelap muncul
 *   5. Finish    cincin orbit tergambar bersama percikan cahaya, lalu "powered by alita" muncul
 *
 * Frame terakhir identik dengan file PNG aslinya.
 * Tanpa library tambahan, hanya WebGL bawaan browser.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

// ==== ENGINE START ====

/** Durasi default seluruh animasi (ms). */
export const DEFAULT_DURATION = 3200;

/**
 * Batas tiap fase, sebagai pecahan dari durasi total (0 = awal, 1 = akhir).
 * Ubah angka di sini untuk mengatur ritme animasi.
 */
export const TIMING = {
  dropsEnd: 0.18, // tetesan selesai bergabung
  flowEnd: 0.44, // aliran selesai mengitari orbit
  fillEnd: 0.68, // semua huruf terisi cairan
  solidifyStart: 0.6, // cairan mulai berubah jadi logo asli
  solidifyEnd: 0.86, // logo utama sudah solid
  finishStart: 0.8, // cincin orbit mulai tergambar
} as const;

/** Warna cairan (RGB 0..1). Base #F26B0A, highlight #FFA23A, deep #B83A00. */
const LIQUID = {
  base: [0.949, 0.42, 0.039],
  highlight: [1.0, 0.635, 0.227],
  deep: [0.722, 0.227, 0.0],
} as const;

/**
 * Jalur orbit (elips) dalam satuan "tinggi gambar":
 * x diukur dari kiri, y dari bawah. angle dalam radian.
 * end = seberapa jauh cincin digambar (0..1 dari satu putaran).
 * Nilai default sudah diukur dari logo AiBAS.
 */
export type Orbit = { cx: number; cy: number; a: number; b: number; angle: number; end: number };
export const AIBAS_ORBIT: Orbit = { cx: 1.4313, cy: 0.5466, a: 0.9937, b: 0.1698, angle: 0.1847, end: 0.76 };

export type LiquidLogoOptions = {
  src: string;
  aspectRatio: number;
  duration: number;
  loop: boolean;
  loopDelay: number;
  playOnView: boolean;
  orbit: Orbit;
  onFallback: () => void;
};

export type LiquidLogoHandle = { play: () => void; dispose: () => void };

const f = (n: number) => n.toFixed(4);
const v3 = (c: readonly number[]) => `vec3(${c.map(f).join(",")})`;

const VS = `attribute vec2 a;varying vec2 v;void main(){v=a*0.5+0.5;gl_Position=vec4(a,0.0,1.0);}`;

const FS = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
varying vec2 v;
uniform sampler2D uTex;
uniform sampler2D uMask;
uniform float uP, uT, uAsp, uDur, uPx, uSdfScale;
uniform vec4 uOrbit;
uniform vec2 uOrbitB;
uniform vec4 uBox;

const float T_DROPS=${f(TIMING.dropsEnd)};
const float T_FLOW=${f(TIMING.flowEnd)};
const float T_FILL=${f(TIMING.fillEnd)};
const float T_SOLS=${f(TIMING.solidifyStart)};
const float T_SOLE=${f(TIMING.solidifyEnd)};
const float T_FIN=${f(TIMING.finishStart)};
const float PI=3.14159265;
const float TAU=6.28318531;

float hash(float n){return fract(sin(n*127.1+11.7)*43758.5453);}
float seg(float x,float a,float b){return clamp((x-a)/(b-a),0.0,1.0);}
float eOut(float x){float y=1.0-x;return 1.0-y*y*y;}
float eIO(float x){return x<0.5?4.0*x*x*x:1.0-pow(-2.0*x+2.0,3.0)*0.5;}
float smin(float a,float b,float k){float h=max(k-abs(a-b),0.0)/k;return min(a,b)-h*h*k*0.25;}
float smax(float a,float b,float k){return -smin(-a,-b,k);}
float circ(vec2 q,vec2 c,float r){return r<0.002?10.0:length(q-c)-r;}

vec2 orbitPt(float s){
  float th=PI+TAU*s;
  vec2 l=vec2(uOrbit.z*cos(th),uOrbit.w*sin(th));
  float c=cos(uOrbitB.x),sn=sin(uOrbitB.x);
  return uOrbit.xy+vec2(c*l.x-sn*l.y,sn*l.x+c*l.y);
}
float frontX(float p){return mix(uBox.x-0.15,uBox.z+0.2,eIO(seg(p,T_FLOW,T_FILL)));}
float lettersSD(vec2 q){return (texture2D(uMask,vec2(q.x/uAsp,q.y)).r*255.0-128.0)*uSdfScale;}

float field(vec2 q){
  float t=uT,p=uP,d=10.0;

  // 1. Drops: tetesan masuk dan bergabung di titik awal orbit
  if(p<T_FLOW){
    vec2 m0=orbitPt(0.0);
    float pd=seg(p,0.0,T_DROPS);
    float gone=1.0-seg(p,T_DROPS,T_DROPS+0.06);
    for(int i=0;i<10;i++){
      float fi=float(i);
      float dl=hash(fi+1.0)*0.4;
      float u=eOut(seg(pd,dl,1.0));
      vec2 st=vec2(-0.25-0.55*hash(fi*2.3+1.0),0.12+0.76*hash(fi*5.1+3.0));
      vec2 off=(vec2(hash(fi*7.7+4.0),hash(fi*3.3+5.0))-0.5)*vec2(0.22,0.14);
      vec2 c=mix(st,m0+off*(1.0-u),u);
      c.y+=0.035*sin(u*7.0+fi*1.7)*(1.0-u);
      d=smin(d,circ(q,c,(0.032+0.03*hash(fi*9.1+6.0))*gone),0.07);
    }
    float rb=0.1*smoothstep(0.35,1.0,pd)*(1.0-seg(p,T_DROPS,T_DROPS+0.1));
    d=smin(d,circ(q,m0,rb),0.06);
  }

  // 2. Flow: aliran mengitari orbit, lalu terkuras saat mengisi huruf
  if(p>T_DROPS-0.03&&p<T_FILL){
    float head=eIO(seg(p,T_DROPS,T_FLOW));
    float tail=max(0.0,head-0.16*smoothstep(0.0,0.3,head));
    float drain=seg(p,T_FLOW,mix(T_FLOW,T_FILL,0.55));
    tail=mix(tail,head,drain);
    float thick=smoothstep(T_DROPS-0.03,T_DROPS+0.03,p)*(1.0-drain);
    for(int j=0;j<24;j++){
      float fj=float(j)/23.0;
      d=smin(d,circ(q,orbitPt(mix(tail,head,fj)),0.06*mix(0.55,1.0,fj)*thick),0.06);
    }
  }

  // 3. Fill: cairan mengisi huruf dari kiri ke kanan
  if(p>T_FLOW){
    float fu=seg(p,T_FLOW,T_FILL);
    float fx=frontX(p);
    float wob=0.035*sin(q.y*13.0+t*9.0)+0.018*sin(q.y*29.0-t*15.0);
    float behind=max(fx-q.x,0.0);
    float settle=1.0-seg(p,T_SOLS,T_SOLE);
    float L=lettersSD(q);
    L-=0.012*exp(-behind*9.0)*(1.0-fu*fu);
    L+=0.006*sin(behind*30.0-t*18.0)*exp(-behind*2.5)*settle;
    d=smin(d,smax(L,q.x-(fx+wob),0.04),0.035);

    float bs=smoothstep(0.0,0.1,fu)*(1.0-smoothstep(0.7,1.0,fu));
    vec2 br=vec2(0.075,0.21)*bs;
    if(br.x>0.003){
      vec2 bc=vec2(fx-0.02+wob*0.5,(uBox.y+uBox.w)*0.5);
      d=smin(d,(length((q-bc)/br)-1.0)*br.x,0.05);
    }
    for(int i=0;i<5;i++){
      float fi=float(i);
      float p0=mix(T_FLOW,T_FILL,0.12+0.16*fi);
      float tau=(p-p0)*uDur;
      if(tau>0.0&&tau<0.5){
        vec2 c=vec2(frontX(p0)+0.12*tau,uBox.w-0.02+(0.8+0.3*hash(fi+20.0))*tau-2.6*tau*tau);
        d=smin(d,circ(q,c,0.02*(1.0-tau*2.0)),0.03);
      }
    }
  }

  d+=0.005*sin(q.x*25.0+t*7.0)*sin(q.y*21.0-t*5.0)*(1.0-seg(p,T_SOLS,T_SOLE));
  return d;
}

void main(){
  vec2 uv=v;
  vec2 q=vec2(uv.x*uAsp,uv.y);
  float p=uP,t=uT;

  // Bentuk cairan + normal permukaan dari gradien SDF
  float e=0.004;
  float d=field(q);
  float dx=field(q+vec2(e,0.0));
  float dy=field(q+vec2(0.0,e));
  float h0=smoothstep(0.0,0.045,-d);
  vec2 g=vec2(smoothstep(0.0,0.045,-dx)-h0,smoothstep(0.0,0.045,-dy)-h0)/e;
  float live=1.0-seg(p,T_SOLS,T_SOLE);
  float rip=0.35*live*h0;
  vec2 rg=rip*vec2(cos(q.x*38.0-t*12.0+q.y*9.0),cos(q.y*27.0+t*7.0+q.x*6.0));
  vec3 N=normalize(vec3(-g*0.03+rg,1.0));
  float aa=1.0-smoothstep(-uPx,uPx,d);

  // Shading: diffuse + specular + rim (fresnel)
  vec3 Ld=normalize(vec3(-0.45,0.65,0.6));
  float diff=clamp(dot(N,Ld),0.0,1.0);
  float spec=pow(max(dot(reflect(-Ld,N),vec3(0.0,0.0,1.0)),0.0),60.0);
  float fres=pow(1.0-N.z,1.5);
  vec3 col=mix(${v3(LIQUID.deep)},${v3(LIQUID.base)},0.35+0.65*diff);
  col=mix(col,${v3(LIQUID.highlight)},h0*0.35);
  col+=${v3(LIQUID.highlight)}*fres*0.5+vec3(spec*0.85);

  // 4. Solidify: cairan "mendingin" ke warna asli logo, lalu berganti ke gambar asli
  float solid=smoothstep(0.0,1.0,seg(p,T_SOLS,T_SOLE)*1.4-uv.x*0.4);
  vec4 tex=texture2D(uTex,uv);
  vec4 mk=texture2D(uMask,uv);
  vec3 texC=tex.rgb/max(tex.a,0.001);
  col=mix(col,texC*(0.7+0.4*diff)+vec3(spec*0.6),clamp(solid*1.6,0.0,1.0)*tex.a);
  vec4 liq=vec4(clamp(col,0.0,1.0)*aa,aa);

  // 5. Finish: cincin orbit tergambar mengikuti jalurnya, teks kecil memudar masuk
  vec2 rel=q-uOrbit.xy;
  float ca=cos(-uOrbitB.x),sa=sin(-uOrbitB.x);
  vec2 nl=vec2(ca*rel.x-sa*rel.y,sa*rel.x+ca*rel.y)/uOrbit.zw;
  float sp=mod(atan(nl.y,nl.x)-PI,TAU)/TAU;
  float g2=eIO(seg(p,T_FIN,0.97));
  float headS=g2*uOrbitB.y;
  float ringness=1.0-smoothstep(0.15,0.3,abs(length(nl)-1.0));
  float ringRev=1.0-smoothstep(headS-0.02,headS,sp);
  float fadeRev=clamp((p-(T_FIN+0.06+0.06*uv.x))/0.1,0.0,1.0);
  float accRev=max(mix(fadeRev,ringRev,ringness),smoothstep(0.975,1.0,p));
  // Cincin yang belum tergambar tetap gelap seperti halo di sekitarnya (bukan bolong)
  float visC=mix(solid,accRev,mk.g);
  float visA=mix(solid,mix(accRev,max(solid,accRev),ringness),mk.g);

  vec4 o=liq*(1.0-solid)+vec4(tex.rgb*visC,tex.a*visA);

  // Percikan cahaya di ujung cincin
  float on=step(0.001,g2)*(1.0-step(0.999,g2));
  float sd=length(q-orbitPt(headS));
  float glow=clamp((exp(-sd*sd/0.0006)*0.7+exp(-sd*sd/0.00005))*sin(PI*g2)*on,0.0,1.0);
  o.rgb+=vec3(1.0,0.78,0.45)*glow;
  o.a=clamp(o.a+glow*(1.0-o.a),0.0,1.0);
  o.rgb=min(o.rgb,vec3(o.a));
  gl_FragColor=o;
}`;

/* ---------- Distance transform (Felzenszwalb) untuk membuat SDF dari logo ---------- */

const INF = 1e20;

function edt1d(fIn: Float64Array, n: number, d: Float64Array, vIdx: Int32Array, z: Float64Array): void {
  let k = 0;
  vIdx[0] = 0;
  z[0] = -INF;
  z[1] = INF;
  for (let q = 1; q < n; q++) {
    let s = (fIn[q] + q * q - (fIn[vIdx[k]] + vIdx[k] * vIdx[k])) / (2 * q - 2 * vIdx[k]);
    while (s <= z[k]) {
      k--;
      s = (fIn[q] + q * q - (fIn[vIdx[k]] + vIdx[k] * vIdx[k])) / (2 * q - 2 * vIdx[k]);
    }
    k++;
    vIdx[k] = q;
    z[k] = s;
    z[k + 1] = INF;
  }
  k = 0;
  for (let q = 0; q < n; q++) {
    while (z[k + 1] < q) k++;
    const dq = q - vIdx[k];
    d[q] = dq * dq + fIn[vIdx[k]];
  }
}

/** Jarak Euclidean tiap piksel ke piksel "feature" terdekat. */
function edt(feature: Uint8Array, w: number, h: number): Float32Array {
  const n = Math.max(w, h);
  const grid = new Float64Array(w * h);
  for (let i = 0; i < w * h; i++) grid[i] = feature[i] ? 0 : INF;
  const fIn = new Float64Array(n);
  const d = new Float64Array(n);
  const vIdx = new Int32Array(n);
  const z = new Float64Array(n + 1);
  for (let x = 0; x < w; x++) {
    for (let y = 0; y < h; y++) fIn[y] = grid[y * w + x];
    edt1d(fIn, h, d, vIdx, z);
    for (let y = 0; y < h; y++) grid[y * w + x] = d[y];
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) fIn[x] = grid[y * w + x];
    edt1d(fIn, w, d, vIdx, z);
    for (let x = 0; x < w; x++) grid[y * w + x] = d[x];
  }
  const out = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) out[i] = Math.sqrt(grid[i]);
  return out;
}

const not = (m: Uint8Array) => m.map((x) => (x ? 0 : 1));

type MaskData = { data: Uint8Array; w: number; h: number; sdfScale: number; box: [number, number, number, number] };

/**
 * Memisahkan logo jadi dua bagian:
 *  - huruf utama (dibentuk oleh cairan) → disimpan sebagai SDF di channel R
 *  - aksen tipis seperti cincin, garis, dan teks kecil → mask di channel G
 */
function buildMasks(img: HTMLImageElement, aspect: number): MaskData {
  const w = 640;
  const h = Math.max(8, Math.round(w / aspect));
  const RANGE = 32; // px, rentang jarak yang disimpan di SDF
  const OPEN = 4.5; // px, goresan lebih tipis dari ini dianggap aksen

  const cv = document.createElement("canvas");
  cv.width = w;
  cv.height = h;
  const ctx = cv.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("2D canvas unavailable");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, w, h);
  const px = ctx.getImageData(0, 0, w, h).data;

  const bright = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) {
    const m = Math.max(px[i * 4], px[i * 4 + 1], px[i * 4 + 2]);
    bright[i] = (m * px[i * 4 + 3]) / 255 > 140 ? 1 : 0;
  }

  // Morphological opening: hapus bagian tipis, sisakan goresan tebal
  const dIn = edt(not(bright), w, h);
  const eroded = dIn.map((x) => (x > OPEN ? 1 : 0));
  const dEr = edt(new Uint8Array(eroded), w, h);
  const opened = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) opened[i] = dEr[i] <= OPEN ? 1 : 0;

  // Komponen terhubung: simpan yang sebaris dengan huruf terbesar
  const label = new Int32Array(w * h);
  const comps: { area: number; sumY: number; r0: number; r1: number; c0: number; c1: number }[] = [];
  const stack: number[] = [];
  for (let i = 0; i < w * h; i++) {
    if (!opened[i] || label[i]) continue;
    const id = comps.length + 1;
    const c = { area: 0, sumY: 0, r0: h, r1: 0, c0: w, c1: 0 };
    label[i] = id;
    stack.push(i);
    while (stack.length) {
      const j = stack.pop() as number;
      const x = j % w;
      const y = (j - x) / w;
      c.area++;
      c.sumY += y;
      c.r0 = Math.min(c.r0, y);
      c.r1 = Math.max(c.r1, y);
      c.c0 = Math.min(c.c0, x);
      c.c1 = Math.max(c.c1, x);
      const nb = [x > 0 ? j - 1 : -1, x < w - 1 ? j + 1 : -1, y > 0 ? j - w : -1, y < h - 1 ? j + w : -1];
      for (const k of nb) {
        if (k >= 0 && opened[k] && !label[k]) {
          label[k] = id;
          stack.push(k);
        }
      }
    }
    comps.push(c);
  }
  const letters = new Uint8Array(w * h);
  let box: [number, number, number, number] = [0, 0, aspect, 1];
  if (comps.length) {
    const big = comps.reduce((a, b) => (b.area > a.area ? b : a));
    const keep = comps.map((c) => {
      const cy = c.sumY / c.area;
      return cy >= big.r0 && cy <= big.r1;
    });
    let r0 = h, r1 = 0, c0 = w, c1 = 0;
    comps.forEach((c, i) => {
      if (!keep[i]) return;
      r0 = Math.min(r0, c.r0);
      r1 = Math.max(r1, c.r1);
      c0 = Math.min(c0, c.c0);
      c1 = Math.max(c1, c.c1);
    });
    for (let i = 0; i < w * h; i++) if (label[i] && keep[label[i] - 1]) letters[i] = 1;
    box = [(c0 / w) * aspect, 1 - (r1 + 1) / h, ((c1 + 1) / w) * aspect, 1 - r0 / h];
  }

  const dOut = edt(letters, w, h);
  const dLin = edt(not(letters), w, h);
  const accent = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) accent[i] = bright[i] && dOut[i] > 2 ? 1 : 0;
  const dAcc = edt(accent, w, h);

  // Tulis tekstur RGBA, baris dibalik (WebGL membaca dari bawah)
  const data = new Uint8Array(w * h * 4);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const o = ((h - 1 - y) * w + x) * 4;
      const sd = letters[i] ? -(dLin[i] - 0.5) : dOut[i] - 0.5;
      data[o] = Math.max(0, Math.min(255, Math.round(128 + (sd * 127) / RANGE)));
      data[o + 1] = Math.round(255 * Math.max(0, Math.min(1, 1 - (dAcc[i] - 1) / 1.5)));
      data[o + 2] = 0;
      data[o + 3] = 255;
    }
  }
  return { data, w, h, sdfScale: RANGE / 127 / h, box };
}

/* ---------- Mesin animasi (tidak bergantung pada React) ---------- */

export function mountLiquidLogo(canvas: HTMLCanvasElement, o: LiquidLogoOptions): LiquidLogoHandle {
  const reduce = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  let gl: WebGLRenderingContext | null = null;
  let uP: WebGLUniformLocation | null = null;
  let uT: WebGLUniformLocation | null = null;
  let uPx: WebGLUniformLocation | null = null;
  let raf = 0;
  let loopTimer: ReturnType<typeof setTimeout> | undefined;
  let start = 0;
  let ready = false;
  let pending = false;
  let disposed = false;
  let ro: ResizeObserver | undefined;
  let io: IntersectionObserver | undefined;

  const draw = (p: number, t: number) => {
    if (!gl) return;
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.uniform1f(uP, p);
    gl.uniform1f(uT, t);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };
  const showFinal = () => draw(1, o.duration / 1000);

  const frame = (now: number) => {
    const el = now - start;
    const p = Math.min(el / o.duration, 1);
    draw(p, el / 1000);
    if (p < 1) raf = requestAnimationFrame(frame);
    else {
      raf = 0;
      if (o.loop) loopTimer = setTimeout(play, o.loopDelay);
    }
  };

  function play() {
    if (disposed) return;
    if (!ready) {
      pending = true;
      return;
    }
    cancelAnimationFrame(raf);
    clearTimeout(loopTimer);
    start = performance.now();
    raf = requestAnimationFrame(frame);
  }

  const resize = () => {
    if (!gl) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
    canvas.width = w;
    canvas.height = Math.max(1, Math.round(w / o.aspectRatio));
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform1f(uPx, 1.5 / canvas.height);
  };

  const compile = (g: WebGLRenderingContext, type: number, src: string) => {
    const s = g.createShader(type);
    if (!s) throw new Error("createShader failed");
    g.shaderSource(s, src);
    g.compileShader(s);
    if (!g.getShaderParameter(s, g.COMPILE_STATUS)) throw new Error(g.getShaderInfoLog(s) || "shader error");
    return s;
  };

  const setup = (img: HTMLImageElement) => {
    const g = canvas.getContext("webgl", { premultipliedAlpha: true, alpha: true, antialias: false });
    if (!g) throw new Error("WebGL unavailable");
    gl = g;
    const masks = buildMasks(img, o.aspectRatio);

    const prog = g.createProgram();
    if (!prog) throw new Error("createProgram failed");
    g.attachShader(prog, compile(g, g.VERTEX_SHADER, VS));
    g.attachShader(prog, compile(g, g.FRAGMENT_SHADER, FS));
    g.linkProgram(prog);
    if (!g.getProgramParameter(prog, g.LINK_STATUS)) throw new Error(g.getProgramInfoLog(prog) || "link error");
    g.useProgram(prog);

    g.bindBuffer(g.ARRAY_BUFFER, g.createBuffer());
    g.bufferData(g.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), g.STATIC_DRAW);
    const a = g.getAttribLocation(prog, "a");
    g.enableVertexAttribArray(a);
    g.vertexAttribPointer(a, 2, g.FLOAT, false, 0, 0);

    const makeTex = (unit: number) => {
      const t = g.createTexture();
      g.activeTexture(g.TEXTURE0 + unit);
      g.bindTexture(g.TEXTURE_2D, t);
      g.texParameteri(g.TEXTURE_2D, g.TEXTURE_MIN_FILTER, g.LINEAR);
      g.texParameteri(g.TEXTURE_2D, g.TEXTURE_MAG_FILTER, g.LINEAR);
      g.texParameteri(g.TEXTURE_2D, g.TEXTURE_WRAP_S, g.CLAMP_TO_EDGE);
      g.texParameteri(g.TEXTURE_2D, g.TEXTURE_WRAP_T, g.CLAMP_TO_EDGE);
    };
    makeTex(0);
    g.pixelStorei(g.UNPACK_FLIP_Y_WEBGL, true);
    g.pixelStorei(g.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    g.texImage2D(g.TEXTURE_2D, 0, g.RGBA, g.RGBA, g.UNSIGNED_BYTE, img);
    makeTex(1);
    g.pixelStorei(g.UNPACK_FLIP_Y_WEBGL, false);
    g.pixelStorei(g.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    g.texImage2D(g.TEXTURE_2D, 0, g.RGBA, masks.w, masks.h, 0, g.RGBA, g.UNSIGNED_BYTE, masks.data);

    const u = (n: string) => g.getUniformLocation(prog, n);
    g.uniform1i(u("uTex"), 0);
    g.uniform1i(u("uMask"), 1);
    g.uniform1f(u("uAsp"), o.aspectRatio);
    g.uniform1f(u("uDur"), o.duration / 1000);
    g.uniform1f(u("uSdfScale"), masks.sdfScale);
    g.uniform4f(u("uOrbit"), o.orbit.cx, o.orbit.cy, o.orbit.a, o.orbit.b);
    g.uniform2f(u("uOrbitB"), o.orbit.angle, o.orbit.end);
    g.uniform4f(u("uBox"), ...masks.box);
    uP = u("uP");
    uT = u("uT");
    uPx = u("uPx");
  };

  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onerror = () => !disposed && o.onFallback();
  img.onload = () => {
    if (disposed) return;
    try {
      setup(img);
    } catch (e) {
      console.error(e);
      o.onFallback();
      return;
    }
    ready = true;
    resize();
    draw(0, 0);
    ro = new ResizeObserver(() => {
      resize();
      if (!raf) showFinal();
    });
    ro.observe(canvas);

    if (reduce) return showFinal();
    if (pending || !o.playOnView || !("IntersectionObserver" in window)) return play();
    io = new IntersectionObserver(
      (entries) => {
        if (entries.some((en) => en.isIntersecting)) {
          play();
          io?.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    io.observe(canvas);
  };
  img.src = o.src;

  return {
    play,
    dispose() {
      disposed = true;
      cancelAnimationFrame(raf);
      clearTimeout(loopTimer);
      ro?.disconnect();
      io?.disconnect();
      gl?.getExtension("WEBGL_lose_context")?.loseContext();
      gl = null;
    },
  };
}

// ==== ENGINE END ====

/* ---------- Komponen React ---------- */

type Props = {
  /** Path logo PNG transparan, mis. "/aibas-logo.png" (taruh di folder /public) */
  src: string;
  alt?: string;
  /** Lebar / tinggi gambar asli. Default: logo AiBAS (2158 x 729) */
  aspectRatio?: number;
  /** Durasi total animasi (ms) */
  duration?: number;
  loop?: boolean;
  /** Jeda antar putaran saat loop (ms) */
  loopDelay?: number;
  /** true: mulai saat logo terlihat di layar. false: langsung saat halaman dibuka */
  playOnView?: boolean;
  /** Klik logo untuk memutar ulang */
  replayOnClick?: boolean;
  /** Jalur orbit. Hanya perlu diubah kalau logonya diganti */
  orbit?: Orbit;
  className?: string;
  style?: CSSProperties;
};

export default function LiquidLogoForm({
  src,
  alt = "Logo",
  aspectRatio = 2158 / 729,
  duration = DEFAULT_DURATION,
  loop = false,
  loopDelay = 1200,
  playOnView = true,
  replayOnClick = true,
  orbit = AIBAS_ORBIT,
  className,
  style,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const handleRef = useRef<LiquidLogoHandle | null>(null);
  const [fallback, setFallback] = useState(false);
  const { cx, cy, a, b, angle, end } = orbit;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handle = mountLiquidLogo(canvas, {
      src,
      aspectRatio,
      duration,
      loop,
      loopDelay,
      playOnView,
      orbit: { cx, cy, a, b, angle, end },
      onFallback: () => setFallback(true),
    });
    handleRef.current = handle;
    return () => handle.dispose();
  }, [src, aspectRatio, duration, loop, loopDelay, playOnView, cx, cy, a, b, angle, end]);

  const replay = useCallback(() => handleRef.current?.play(), []);

  if (fallback) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img src={src} alt={alt} className={className} style={{ display: "block", width: "100%", height: "auto", ...style }} />
    );
  }

  return (
    <canvas
      ref={canvasRef}
      role="img"
      aria-label={alt}
      onClick={replayOnClick ? replay : undefined}
      className={className}
      style={{
        display: "block",
        width: "100%",
        height: "auto",
        aspectRatio: String(aspectRatio),
        cursor: replayOnClick ? "pointer" : undefined,
        ...style,
      }}
    />
  );
}
